import styled from '@emotion/styled'

import { breakpoint, calcRem, breakpointMax } from 'component-library/__styling/style-functions'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

interface GridProps {
    initialColumns: number
    finalColumns: number
    verticalSpacing: keyof ThemeMargins | undefined
}

export const StyledWhiskey = styled('div')<GridProps>(
    {
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 1,
        position: 'relative',
    },
    ({ verticalSpacing, theme }) => ({
        marginBottom: verticalSpacing && calcRem(theme.margins[verticalSpacing]),
        height: undefined,
    })
)
StyledWhiskey.displayName = 'StyledWhiskey'
export const StyledHeader = styled('div')(
    {
        display: 'block',
    },
    props => ({
        marginBottom: calcRem(props.theme.margins.md),
    })
)
StyledHeader.displayName = 'StyledHeader'

export interface CellProps {
    numberOfItems: number
    initialColumns: number
    intermediateColumns: number
    finalColumns: number
}

const getLastRowOfCards = (numberOfItems: number, columns: number) => {
    const calcItems = (numberOfItems / columns) % 1
    const lastRowOfCards = calcItems > 0 ? calcItems * columns : columns

    return lastRowOfCards
}

const getInitialWidth = (initialColumns: number) => {
    const cellWidth = 100 / initialColumns
    return cellWidth
}

const getWidth = (columns: number) => {
    const cellWidth = 100 / columns
    return cellWidth
}

export const StyledGridItem = styled('div')<CellProps>(
    {
        display: 'flex',
        justifyContent: 'stretch',
        flexDirection: 'column',
    },

    props => {
        const { theme, initialColumns, intermediateColumns, finalColumns } = props
        const numberOfItems = props.numberOfItems + 1 // add 1 to include the static card in the first position

        return {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: initialColumns > 1 ? `auto` : `100%`,
            width: `calc(${getInitialWidth(initialColumns) + '%'} - 19px)`,
            marginRight: initialColumns > 1 ? calcRem(theme.margins.gridGap) : 0,
            marginBottom: calcRem(theme.margins.gridGap),

            '& > *:not(:last-child)': {
                marginBottom: calcRem(theme.margins.gridGap),
            },

            // Not in lima: These breakpoints are here because the mobile version has the
            // static first card spanning two columns so we need to offset
            // the margins by 1 card
            [breakpointMax('sm')]: {
                [':nth-child(' + initialColumns + 'n - 1)']: {
                    marginRight: '0',
                },
            },
            [breakpoint('sm')]: {
                [':nth-child(' + initialColumns + 'n)']: {
                    marginRight: '0',
                },
            },

            // Removes top margin from top row
            [':nth-child(-n+' + initialColumns + ')']: { marginTop: 0 },

            // Removed from lima: removing bottom margin

            [breakpoint('sm')]: {
                flexBasis: `auto`,
                marginRight: calcRem(theme.margins.gridGap),
                width: `calc(${getWidth(intermediateColumns) + '%'} - 19px)`,

                [':nth-child(' + initialColumns + 'n)']: {
                    marginRight: calcRem(theme.margins.gridGap),
                },

                // Reset
                [':nth-child(' + intermediateColumns + 'n)']: { marginRight: 0 },
                [':last-child']: {
                    marginRight: 0,
                },

                // Remove Top margin from top row
                [':nth-child(-n+' + intermediateColumns + ')']: {
                    marginTop: 0,
                },

                // prettier-ignore
                [':nth-last-child(-n+' + getLastRowOfCards(numberOfItems, intermediateColumns) + ')']: {
                    // TODO this only works for the situation where initial/intermediate/final columns are 2/4/7
                    // if we decide to use this collection a different number of columns this will need fixing
                    flexGrow: 0, // not in lima
                    width: `calc(${getInitialWidth(intermediateColumns) + '%'} - 9px)`, // not in lima
                    marginBottom: 0,
                }
            },

            [breakpoint('md')]: {
                // prettier-ignore
                width: `calc(${getWidth(finalColumns) + '%'} - ${theme.margins.gridGap + 1 + 'px'})`,

                [':nth-child(' + initialColumns + 'n)']: {
                    marginRight: calcRem(theme.margins.gridGap),
                },

                // Reset
                [':nth-child(' + finalColumns + 'n)']: { marginRight: 0 },
                [':last-child']: {
                    marginRight: 0,
                },

                // Remove Top margin from top row
                [':nth-child(-n+' + finalColumns + ')']: { marginTop: 0 },

                // Remove bottom margin from bottom row
                // prettier-ignore
                [':nth-last-child(-n+' + getLastRowOfCards(numberOfItems, finalColumns) + ')']: {
                    marginBottom: 0,
                    flexGrow: 1, // not in lima
                    width: `calc(${getWidth(finalColumns) + '%'} - ${theme.margins.gridGap + 1 + 'px'})`, // not in lima
                }
            },
        }
    }
)
